/**
 * =============================================================================
 * Hook: useAffiliateCommissions
 * Versão: 2.0.0 (Server-side hardening)
 *
 * Mudanças v2.0.0:
 *   - Substitui o SELECT direto em `affiliate_commissions` pela RPC
 *     `get_affiliate_commissions_page`. Servidor decide quais linhas o
 *     afiliado pode ver e devolve apenas o necessário (sem `payout_id`,
 *     sem `notes` internas, sem `metadata`).
 *   - Mantém o shape esperado pela página `/afiliado/comissoes` (nome do
 *     lead, nome da campanha, nome do programa).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useAffiliateCommissions');

export type CommissionStatus = Database['public']['Enums']['affiliate_commission_status'];
export type RewardType = Database['public']['Enums']['affiliate_reward_type'];

export interface AffiliateCommissionRow {
  id: string;
  status: CommissionStatus;
  reward_type: RewardType;
  reward_value: number;
  base_value: number;
  currency: string;
  points_earned: number;
  created_at: string;
  approved_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  notes: string | null;
  referral_id: string;
  campaign_id: string | null;
  referral: { id: string; lead_name: string | null } | null;
  campaign: { id: string; name: string } | null;
  program_id: string;
  program: { id: string; name: string } | null;
}

interface RpcRow {
  id_short: string;
  status: string;
  reward_type: string;
  reward_value: number;
  base_value: number;
  currency: string;
  points_earned: number;
  created_at_iso: string;
  approved_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  lead_name: string | null;
  campaign_name: string | null;
  program_id: string;
  program_name: string | null;
}

export function useAffiliateCommissions() {
  const { affiliate, ready } = useAffiliateAuth();

  return useQuery({
    queryKey: ['affiliate-commissions', affiliate?.id],
    enabled: ready && !!affiliate?.id,
    queryFn: async (): Promise<AffiliateCommissionRow[]> => {
      const { data, error } = await supabase.rpc('get_affiliate_commissions_page', {
        p_status: null,
        p_program_id: null,
        p_period_start: null,
        p_period_end: null,
        p_limit: 200,
        p_offset: 0,
      });

      if (error) {
        log.error('Falha ao listar comissões via RPC', error);
        throw error;
      }

      const payload = data as unknown as { rows: RpcRow[]; total: number };
      const rows = payload?.rows ?? [];

      return rows.map<AffiliateCommissionRow>((r) => ({
        id: r.id_short,
        status: r.status as CommissionStatus,
        reward_type: r.reward_type as RewardType,
        reward_value: Number(r.reward_value ?? 0),
        base_value: Number(r.base_value ?? 0),
        currency: r.currency,
        points_earned: r.points_earned,
        created_at: r.created_at_iso,
        approved_at: r.approved_at,
        paid_at: r.paid_at,
        cancelled_at: r.cancelled_at,
        cancelled_reason: r.cancelled_reason,
        notes: null,
        referral_id: '',
        campaign_id: null,
        referral: r.lead_name ? { id: '', lead_name: r.lead_name } : null,
        campaign: r.campaign_name ? { id: '', name: r.campaign_name } : null,
        program_id: r.program_id,
        program: r.program_name ? { id: r.program_id, name: r.program_name } : null,
      }));
    },
    staleTime: 30_000,
  });
}
